// Ova Balance buying guide. Hand-built article page: needs inline citation
// links, branded figures with captions and product CTAs the CMS block model
// cannot express.
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ChevronDown, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { ClusterLinks } from "@/components/ClusterLinks";
import { RelatedReading } from "@/components/RelatedReading";
import { relatedArticles } from "@/lib/clusters";
import headerImg from "@/assets/blog/ova-buying-header.png.asset.json";
import formulaImg from "@/assets/blog/ova-sachet-formula.png.asset.json";
import routineImg from "@/assets/blog/ova-daily-routine.png.asset.json";

const SLUG = "where-to-buy-inositol-for-pcos-supplements-online-india";
const URL = `https://truwe.in/blog/${SLUG}`;
const TITLE = "Where to Buy Inositol For PCOS in India | TruWe";
const DESCRIPTION =
  "Buy Ova Balance online in India. Explore its 40:1 inositol formula, compare ingredients and serving size, and check current pricing and delivery on TruWe.";
const EXCERPT =
  "A ratio on the pack is where comparison starts, not where it ends. How to read an inositol label before you order online in India.";
export const PUBLISHED_AT = "2026-09-14";

const GUIDELINE =
  "https://www.monash.edu/__data/assets/pdf_file/0004/3379155/PCOS_Evidence-Based-Guveline-2023.pdf";
const NCCIH = "https://www.nccih.nih.gov/health/berberine-and-weight-loss-what-you-need-to-know";

const FAQS = [
  {
    q: "Is the 40 to 1 ratio the best option for everyone with PCOS?",
    a: "The international guideline does not endorse a particular inositol type, dose or combination because the evidence is insufficient. A ratio is a formula detail to discuss with your clinician, not a guarantee of results.",
    source: { label: "PCOS guideline 2023", url: GUIDELINE },
  },
  {
    q: "Will inositol make my periods regular by a fixed date?",
    a: "Do not base a purchase on a guaranteed cycle timeline. The evidence has limitations, and individual care needs differ. Agree with your clinician on what to monitor and when to review your plan.",
  },
  {
    q: "Can Ova Balance replace prescribed PCOS treatment?",
    a: "Do not replace or adjust prescribed treatment on the basis of a product page. Show your clinician the complete formula and discuss whether it has a place alongside your existing care.",
  },
  {
    q: "Where can I buy Ova Balance and check delivery?",
    a: "Buy Ova Balance through TruWe's official product page. Check the delivery information for your address and the dispatch terms shown when you order. Availability does not imply same-day dispatch or a guaranteed arrival date.",
  },
] as const;

const TOC = [
  { id: "care-plan", label: "Begin with your own care plan" },
  { id: "amount-and-ratio", label: "Read the amount as well as the ratio" },
  { id: "what-it-contains", label: "What Ova Balance contains" },
  { id: "extra-ingredients", label: "Why the extra ingredients matter" },
  { id: "before-ordering", label: "What to check before ordering online" },
  { id: "cost-per-day", label: "Compare the cost per day" },
  { id: "faq", label: "Questions buyers ask" },
] as const;

export const Route = createFileRoute(
  "/blog/where-to-buy-inositol-for-pcos-supplements-online-india",
)({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      ogTitle: "Where to Buy Inositol Supplements Online in India",
      ogDescription: EXCERPT,
      headline: "Where to Buy Inositol Supplements Online in India",
      breadcrumbName: "Where to buy inositol supplements online in India",
      datePublished: PUBLISHED_AT,
      dateModified: PUBLISHED_AT,
      articleType: "BlogPosting",
      authorName: "TruWe Editorial",
      ogImage: `https://truwe.in${headerImg.url}`,
      faqs: FAQS.map((f) => ({ q: f.q, a: f.a })),
    }),
});

function Src({ label, url }: { label: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener nofollow"
      className="text-brand underline underline-offset-2"
    >
      {label}
    </a>
  );
}

function Figure({
  src,
  alt,
  caption,
  width,
  height,
  eager,
}: {
  src: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
  eager?: boolean;
}) {
  return (
    <figure className="mt-8">
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? "eager" : "lazy"}
        // eslint-disable-next-line react/no-unknown-property
        fetchPriority={eager ? "high" : "auto"}
        decoding="async"
        className="h-auto w-full rounded-3xl border border-border object-contain"
      />
      <figcaption className="mt-2 text-xs text-muted-foreground">{caption}</figcaption>
    </figure>
  );
}

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2
      id={id}
      className="mt-12 scroll-mt-28 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl"
    >
      {children}
    </h2>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-4 text-[17px] leading-8 text-foreground/85 md:text-[18px]">{children}</p>;
}

function ProductLink({ children }: { children: React.ReactNode }) {
  return (
    <Link
      to="/products/$handle"
      params={{ handle: "ova-balance" }}
      className="text-brand underline underline-offset-2"
    >
      {children}
    </Link>
  );
}

function Article() {
  return (
    <article className="bg-background pb-28 pt-28 md:pt-36" data-article-slug={SLUG}>
      <div className="mx-auto max-w-[720px] px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            TruWe · Ova Balance buying guide
          </p>
          <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Where to Buy Inositol Supplements Online in India
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="font-medium text-brand-deep">By TruWe Editorial</span>
            <span aria-hidden>·</span>
            <time dateTime={PUBLISHED_AT}>14 September 2026</time>
            <span aria-hidden>·</span>
            <span className="rounded-full bg-brand/10 px-2 py-0.5 font-semibold text-brand">
              TruWe educational product guide
            </span>
          </div>
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>

          <Figure
            eager
            src={headerImg.url}
            width={1672}
            height={941}
            alt="TruWe Ova Balance 30-sachet pack with a single sachet, beside the words Buying inositol online? Start with the formula."
            caption="Buying inositol online: start with the formula."
          />

          <P>
            You can buy Ova Balance directly from TruWe&apos;s official website when shopping
            for inositol supplements online in India. Our 30-sachet formula combines
            myo-inositol and D-chiro-inositol in a 40:1 ratio with berberine, folate and
            additional nutrients. Review the complete ingredients, current price and delivery
            terms before placing your order. <ProductLink>Ova Balance product details</ProductLink>
          </P>
          <P>
            When you are comparing inositol products, a ratio on the pack may be the first
            thing you notice. It should be the beginning of your comparison. The daily amount,
            other active ingredients and the reason you are considering supplementation all
            deserve attention before you commit to a box.
          </P>

          <nav
            aria-label="On this page"
            className="mt-8 rounded-2xl border border-border bg-card p-4"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              On this page
            </p>
            <ul className="mt-2 grid gap-1 sm:grid-cols-2">
              {TOC.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className="text-sm text-muted-foreground underline-offset-2 hover:text-brand hover:underline"
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="care-plan">Begin with your own care plan</H2>
          <P>
            If you have PCOS, you may already be managing appointments, food choices and a
            growing list of supplement suggestions. A useful purchase should be easy to
            explain: what is in it, why you are considering it and what your clinician thinks
            about its place in your care.
          </P>
          <P>
            The 2023 international PCOS guideline says inositol may be considered according to
            individual preferences, while noting limited clinical benefits and uncertainty in
            the evidence. It does not recommend a specific type, dose or combination.{" "}
            <Src label="PCOS guideline 2023" url={GUIDELINE} /> Keep those limits in view when
            reading confident online claims about cycles, weight or fertility.
          </P>
          <P>
            If you are investigating irregular periods without a diagnosis, arrange an
            assessment rather than selecting a supplement to explain the symptoms. A product
            search cannot tell you why your cycle has changed.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="amount-and-ratio">Read the amount as well as the ratio</H2>
          <P>
            Myo-inositol and D-chiro-inositol may appear together on a label. A 40:1 ratio
            means there are 40 parts myo-inositol for each part D-chiro-inositol. It does not
            tell you the total quantity or how often the product is taken.
          </P>
          <P>
            Two products can use that ratio while supplying different amounts per day. Compare
            the quantity of each ingredient per serving, the serving frequency and the complete
            daily intake. If a brand cites a study, check whether the study used the same
            quantities and schedule. Do not increase the serving yourself to match a paper.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="what-it-contains">What Ova Balance contains</H2>
          <Figure
            src={formulaImg.url}
            width={1536}
            height={1024}
            alt="Ova Balance selected ingredients per sachet: myo-inositol 2,000 mg, D-chiro-inositol 50 mg, Berberine HCl 300 mg, L-5-MTHF folate 400 mcg."
            caption="Selected ingredients shown. Read the full product label."
          />
          <P>
            Our Ova Balance lists 2,000 mg myo-inositol and 50 mg D-chiro-inositol per sachet,
            alongside berberine HCl, L-5-MTHF folate and additional nutrients. The pack
            contains 30 sachets, with a listed serving of one sachet daily. Check the full
            current panel and follow the final pack directions.{" "}
            <ProductLink>Ova Balance product details</ProductLink>
          </P>
          <P>
            This is a combination formula, so assess every ingredient when deciding whether it
            suits you. If your clinician has suggested inositol alone, show them the Ova Balance
            panel before choosing it. A sachet format may suit someone who prefers a powder
            routine, but the format does not establish a clinical advantage.
          </P>
          <P>
            The composition gives you specific information to discuss. It does not mean that
            the complete product has been proven to deliver every result reported in research
            on its individual ingredients.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="extra-ingredients">Why the extra ingredients matter</H2>
          <div className="mt-4 rounded-2xl border border-border bg-card p-5">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              <ShieldCheck className="h-3.5 w-3.5" /> Safety
            </p>
            <P>
              Added ingredients are part of the decision, even when inositol is the reason you
              opened the page. Berberine can interact with medicines, and NCCIH advises that it
              may be unsafe during pregnancy or breastfeeding.{" "}
              <Src label="NCCIH: berberine" url={NCCIH} /> Because Ova Balance contains
              berberine, do not treat it as interchangeable with an inositol-only product or a
              prenatal supplement.
            </P>
            <P>
              If you are trying to conceive, pregnant, breastfeeding or taking medicines,
              discuss the complete formula with your clinician before considering it. Bring a
              list of any other supplements as well. This gives you a clearer view of
              overlapping ingredients and whether a different option would better match your
              care plan.
            </P>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="before-ordering">What to check before ordering online</H2>
          <P>
            Start with the current label rather than a social post or an older screenshot.
            Confirm the amounts per sachet, the suggested daily serving and the complete list of
            ingredients. If an amount is expressed only as a blend total, ask what information
            is available about its components.
          </P>
          <P>Then check the purchase details:</P>
          <ul className="mt-4 space-y-2 text-[17px] leading-8 text-foreground/85 md:text-[18px]">
            <li className="flex gap-3">
              <span aria-hidden className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              The identity of the actual seller and how to contact them.
            </li>
            <li className="flex gap-3">
              <span aria-hidden className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              Current stock availability and the quantity you are ordering.
            </li>
            <li className="flex gap-3">
              <span aria-hidden className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              The total payable amount, payment options and cancellation terms.
            </li>
            <li className="flex gap-3">
              <span aria-hidden className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              Dispatch information, delivery charges and the returns policy.
            </li>
            <li className="flex gap-3">
              <span aria-hidden className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              Batch, expiry and storage details on the pack once received.
            </li>
          </ul>
          <P>
            For Ova Balance, order through the official TruWe product page. Review the available
            pack options, enter your delivery details and check the total at checkout. Keep your
            order confirmation so you can refer to it if you need help with your purchase.{" "}
            <ProductLink>Ova Balance product details</ProductLink>
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="cost-per-day">Compare the cost per day</H2>
          <Figure
            src={routineImg.url}
            width={1536}
            height={1024}
            alt="A woman pouring a TruWe Ova Balance sachet into a glass of water on a kitchen counter, beside the 30-sachet pack."
            caption="One sachet daily, mixed into water. Follow the directions on your pack."
          />
          <P>
            Calculate daily cost using the labelled serving. A larger box may last fewer days
            than you expect if a serving contains multiple sachets. Compare daily amounts and
            ingredients alongside the price, so you know what the difference actually buys.
          </P>
          <P>
            Use the current selling price on the official product page. At the listed serving of
            one sachet daily, a 30-sachet box provides 30 servings; divide the box price by 30
            to compare the daily cost. Check delivery charges and any offer conditions at
            checkout. Choose the quantity that fits your agreed routine.{" "}
            <ProductLink>Ova Balance product details</ProductLink>
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <h2
            id="faq"
            className="mt-14 scroll-mt-28 font-display text-2xl font-extrabold text-brand-deep md:text-3xl"
          >
            Questions buyers ask
          </h2>
          <div className="mt-4 space-y-3">
            {FAQS.map((f) => (
              <details key={f.q} className="group rounded-2xl border border-border bg-card p-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-display text-base font-bold text-brand-deep">
                  {f.q}
                  <ChevronDown
                    aria-hidden
                    className="h-4 w-4 shrink-0 text-brand transition group-open:rotate-180"
                  />
                </summary>
                <p className="mt-2 text-[16px] leading-7 text-muted-foreground">
                  {f.a}{" "}
                  {"source" in f && f.source ? (
                    <Src label={f.source.label} url={f.source.url} />
                  ) : null}
                </p>
              </details>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <section className="mt-14 rounded-3xl border border-brand/20 bg-brand/[0.04] p-6 md:p-8">
            <h2 className="font-display text-xl font-extrabold text-brand-deep md:text-2xl">
              Choose Ova Balance for your daily routine
            </h2>
            <P>
              If you and your clinician have reviewed the complete formula and it fits your care
              plan, you can order Ova Balance directly from TruWe. Check the ingredients once
              more against your existing supplements, choose your pack and follow the labelled
              directions. Your next step can be a clear, informed purchase.
            </P>
            <Link
              to="/products/$handle"
              params={{ handle: "ova-balance" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-deep"
            >
              Shop Ova Balance on TruWe <ArrowRight className="h-4 w-4" />
            </Link>
          </section>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-14 font-display text-lg font-bold text-brand-deep">Sources</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            <li>
              <ProductLink>TruWe Ova Balance product details</ProductLink>
            </li>
            <li>
              <Src
                label="International Evidence-Based Guideline for PCOS 2023, Section 4.7"
                url={GUIDELINE}
              />
            </li>
            <li>
              <Src label="NCCIH: Berberine and Weight Loss — What You Need To Know" url={NCCIH} />
            </li>
          </ol>

          <ClusterLinks cluster="hormonal" />
          <RelatedReading articles={relatedArticles(SLUG)} />
          <p className="mt-12 text-xs italic leading-6 text-muted-foreground">
            This is a TruWe educational product guide, not personal medical advice. Ova Balance
            is a nutraceutical and is not a substitute for a balanced diet or medical treatment.
            Images are illustrative.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
